// ============================================================
// PERRY — WORLD TEXTURE KIT
// The generic worldgen helpers, extracted 2026-07-28 when the
// second biome arrived.
//
// WHY THIS FILE EXISTS. These five things — seeded value noise,
// fbm, a sobel normal-mapper, a blurred height canvas, and the
// instance filler — were written inside CinematicStage for the
// Cinders Road, and every one of them is completely biome-blind.
// The Drowned Mile needs all five. Copying them into a second
// file would have produced two implementations of the same
// mathematics, and the codex is explicit about what happens next:
// two hand-maintained copies WILL drift, and the one that drifts
// is the one nobody is looking at.
//
// So: one copy, both worlds. `CinematicStage` and `FenWorld` both
// import from here and neither owns it.
//
// LAWS CARRIED IN WITH THEM, unchanged:
//   • mulberry32 for everything — a road must look the same twice.
//   • Canvas textures cached at MODULE scope and tagged
//     SRGBColorSpace, except normal maps, which are DATA and must
//     never be colour-managed.
//   • InstancedMesh filled once, guarded by userData.filled.
// ============================================================

import * as THREE from "three";
import { mulberry32 } from "./qteEngine";

// A module-scope cache every biome shares. Keys are namespaced by
// the biome that made them ("fenWater", "roadAlbedo") so two
// worlds can never hand each other the wrong canvas.
export const TEX = {};

// ── SEEDED VALUE NOISE — the road must look the same twice ────
export function makeNoise(seed) {
  const R = mulberry32(seed);
  const G = 256;
  const grid = new Float32Array(G * G);
  for (let i = 0; i < G * G; i++) grid[i] = R();
  const at = (x, y) => grid[((y & 255) * G) + (x & 255)];
  return (x, y) => {
    const xi = Math.floor(x), yi = Math.floor(y);
    const fx = x - xi, fy = y - yi;
    const u = fx * fx * (3 - 2 * fx);
    const v = fy * fy * (3 - 2 * fy);
    const a = at(xi, yi), b = at(xi + 1, yi);
    const c = at(xi, yi + 1), d = at(xi + 1, yi + 1);
    return (a * (1 - u) + b * u) * (1 - v) + (c * (1 - u) + d * u) * v;
  };
}
export function fbm(n, x, y, oct = 4) {
  let s = 0, amp = 0.5, f = 1, norm = 0;
  for (let i = 0; i < oct; i++) {
    s += n(x * f, y * f) * amp;
    norm += amp;
    amp *= 0.5; f *= 2.05;
  }
  return s / norm;
}
export const NOISE = makeNoise(1337);

// ── SOBEL → NORMAL MAP ────────────────────────────────────────
// A height canvas in, a tangent-space normal map out. This is the
// whole reason the ash reads as ash instead of a brown plane.
// NOT tagged SRGBColorSpace on purpose — a normal map is data.
export function normalMapFrom(hc, strength = 2.4) {
  const w = hc.width, h = hc.height;
  const src = hc.getContext("2d").getImageData(0, 0, w, h).data;
  const out = document.createElement("canvas");
  out.width = w; out.height = h;
  const g = out.getContext("2d");
  const img = g.createImageData(w, h);
  const H = (x, y) => src[((((y + h) % h) * w + ((x + w) % w)) * 4)] / 255;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const dx = (H(x + 1, y) - H(x - 1, y)) * strength;
      const dy = (H(x, y + 1) - H(x, y - 1)) * strength;
      const nx = -dx, ny = -dy, nz = 1;
      const l = Math.sqrt(nx * nx + ny * ny + 1) || 1;
      const i = (y * w + x) * 4;
      img.data[i] = (nx / l * 0.5 + 0.5) * 255;
      img.data[i + 1] = (ny / l * 0.5 + 0.5) * 255;
      img.data[i + 2] = (nz / l * 0.5 + 0.5) * 255;
      img.data[i + 3] = 255;
    }
  }
  g.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(out);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

// a grayscale height canvas built out of blurred blobs — fast,
// organic, and canvas does the blurring on the GPU for us
export function heightCanvas(size, seed, blobs, big) {
  const c = document.createElement("canvas");
  c.width = size; c.height = size;
  const g = c.getContext("2d");
  const R = mulberry32(seed);
  g.fillStyle = "#808080"; g.fillRect(0, 0, size, size);
  for (let i = 0; i < blobs; i++) {
    const x = R() * size, y = R() * size;
    const r = (0.02 + R() * big) * size;
    const up = R() > 0.5;
    const rg = g.createRadialGradient(x, y, 1, x, y, r);
    rg.addColorStop(0, up ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.5)");
    rg.addColorStop(1, "rgba(128,128,128,0)");
    g.fillStyle = rg;
    g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill();
  }
  return c;
}


// ── SOFT FALLOFF — the two textures that stop a glow being a box ──
// A `spriteMaterial` with no map draws a FLAT OPAQUE SQUARE, and a
// `planeGeometry` with a flat colour draws a hard-edged rectangle.
// Both are technically "a glow" and neither looks like one — this
// is why an unfinished scene reads as untextured confetti and grey
// slabs rather than atmosphere.
//
// Two 128² canvases, cached at module scope like everything else:
//   softSprite  radial, for motes, wisps, will-o-lights, embers
//   softBand    a horizontal ribbon, dense in the middle, gone at
//               all four edges — for mist and fog curtains
// Both are alpha only; the material's `color` does the colouring,
// so ONE texture serves every hue in the Empire.
export function softSprite() {
  if (TEX.softSprite) return TEX.softSprite;
  const c = document.createElement("canvas");
  c.width = 128; c.height = 128;
  const g = c.getContext("2d");
  const rg = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  // a hot little core with a long tail — a linear falloff reads as
  // a disc, and a disc is just a round box
  rg.addColorStop(0.00, "rgba(255,255,255,1)");
  rg.addColorStop(0.18, "rgba(255,255,255,0.72)");
  rg.addColorStop(0.45, "rgba(255,255,255,0.20)");
  rg.addColorStop(0.75, "rgba(255,255,255,0.04)");
  rg.addColorStop(1.00, "rgba(255,255,255,0)");
  g.fillStyle = rg;
  g.fillRect(0, 0, 128, 128);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  TEX.softSprite = t;
  return t;
}

export function softBand() {
  if (TEX.softBand) return TEX.softBand;
  const c = document.createElement("canvas");
  c.width = 256; c.height = 128;
  const g = c.getContext("2d");
  // vertical falloff first — mist is thickest low and thins upward
  const v = g.createLinearGradient(0, 128, 0, 0);
  v.addColorStop(0.00, "rgba(255,255,255,0)");
  v.addColorStop(0.22, "rgba(255,255,255,0.95)");
  v.addColorStop(0.55, "rgba(255,255,255,0.55)");
  v.addColorStop(1.00, "rgba(255,255,255,0)");
  g.fillStyle = v;
  g.fillRect(0, 0, 256, 128);
  // then bite the ends off, or the curtain has two vertical edges
  // hanging in mid-air and the whole illusion goes with them
  g.globalCompositeOperation = "destination-in";
  const h = g.createLinearGradient(0, 0, 256, 0);
  h.addColorStop(0.00, "rgba(0,0,0,0)");
  h.addColorStop(0.18, "rgba(0,0,0,1)");
  h.addColorStop(0.82, "rgba(0,0,0,1)");
  h.addColorStop(1.00, "rgba(0,0,0,0)");
  g.fillStyle = h;
  g.fillRect(0, 0, 256, 128);
  g.globalCompositeOperation = "source-over";
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  TEX.softBand = t;
  return t;
}

// ── SPARSE INSTANCING — thousands of objects, one draw call ───
// TWO ITEM SHAPES, one reader. Fixed 2026-07-29.
//
// The Cinders Pass writes items FLAT — `{ x, y, z, rx, ry, rz,
// sx, sy, sz }`. The Drowned Mile writes them as TUPLES —
// `{ p: [x,y,z], r: [...], s: [...] }`. This function only ever
// understood the flat one, so every tuple item resolved to
// `DUMMY.position.set(undefined, undefined, undefined)` → a NaN
// matrix, and NaN geometry does not draw.
//
// That is why the fen looked empty: the causeway, the doors, the
// trestle piles, sixteen hundred reeds, the mooring posts and the
// sunken things ALL come through here, and every one of them was
// a silent NaN. It never surfaced because `FenWorld` had never
// rendered (R-07) — a whole level's geometry sitting behind two
// property names.
//
// Reading both is four lines and keeps ONE implementation, so
// there is nothing to drift. Scale defaults to 1 rather than
// undefined, because a missing scale should mean "as authored",
// not "collapse to a point".
const DUMMY = new THREE.Object3D();
export function fillInstances(el, items) {
  if (!el || el.userData.filled) return;
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const p = it.p, r = it.r, s = it.s;
    DUMMY.position.set(
      p ? p[0] : it.x,
      p ? p[1] : it.y,
      p ? p[2] : it.z,
    );
    DUMMY.rotation.set(
      (r ? r[0] : it.rx) || 0,
      (r ? r[1] : it.ry) || 0,
      (r ? r[2] : it.rz) || 0,
    );
    DUMMY.scale.set(
      (s ? s[0] : it.sx) ?? 1,
      (s ? s[1] : it.sy) ?? 1,
      (s ? s[2] : it.sz) ?? 1,
    );
    DUMMY.updateMatrix();
    el.setMatrixAt(i, DUMMY.matrix);
  }
  el.instanceMatrix.needsUpdate = true;
  el.userData.filled = true;
}

